import { AxiosError } from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import nutritionistSignupVisual from '@/assets/images/appointments/nutritionist-signup.svg';
import nutritionOnboarding from '@/assets/images/nutrition_onboarding.svg';
import menuBarLogo from '@/assets/images/menu-bar-logo.svg';
import {
  requestForgotPasswordOtp,
  resetForgotPassword,
  verifyForgotPasswordOtp,
} from '@/lib/apiService';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { applyOtpInputValue, applyOtpPasteValue, isOtpSubmitKey } from '@/utils/otp-input';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

const resetPasswordSchema = z.object({
  password: z
    .string()
    .min(12, 'Password must be at least 12 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/\d/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const otpLength = 6;
  const [step, setStep] = useState<'request' | 'otp' | 'reset'>('request');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [otp, setOtp] = useState<string[]>(Array(otpLength).fill(''));
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setResendCooldownSeconds((previous) => Math.max(previous - 1, 0));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [resendCooldownSeconds]);

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const resetPasswordForm = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    setIsSubmitting(true);
    try {
      await requestForgotPasswordOtp({ email: data.email.trim() });
      setSubmittedEmail(data.email.trim());
      setResetToken('');
      setOtp(Array(otpLength).fill(''));
      setStep('otp');
      showSuccessToast('OTP sent', 'Please check your inbox for the verification code.');
    } catch (error) {
      showErrorToast(error, 'Could not send OTP');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendLink = useCallback(async () => {
    if (!submittedEmail || resendCooldownSeconds > 0) {
      return;
    }

    setIsResendingOtp(true);
    try {
      await requestForgotPasswordOtp({ email: submittedEmail });
      showSuccessToast('OTP resent', 'Please check your inbox for the new code.');
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 429) {
        const retryAfterSeconds = Number(error.response.data?.retryAfterSeconds);
        if (retryAfterSeconds > 0) {
          const nextCooldown = Math.ceil(retryAfterSeconds);
          setResendCooldownSeconds(nextCooldown);
          showWarningToast('Please wait', `Try again in ${nextCooldown} seconds.`);
        } else {
          showWarningToast('Please wait', 'Please wait before requesting another reset code.');
        }
        return;
      }

      showErrorToast(error, 'Could not resend OTP');
    } finally {
      setIsResendingOtp(false);
    }
  }, [resendCooldownSeconds, submittedEmail]);

  const handleOtpChange = useCallback((index: number, value: string) => {
    setOtp((previous) => {
      const { next, focusIndex } = applyOtpInputValue(previous, index, value, otpLength);
      window.setTimeout(() => {
        otpRefs.current[focusIndex]?.focus();
        otpRefs.current[focusIndex]?.select?.();
      }, 0);
      return next;
    });
  }, [otpLength]);

  const handleOtpKeyDown = useCallback((index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isOtpSubmitKey(event.key)) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
      return;
    }
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }

    if (event.key === 'ArrowRight' && index < otpLength - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  }, [otp, otpLength]);

  const handleOtpPaste = useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const nextValues = applyOtpPasteValue(event.clipboardData.getData('text'), otpLength);
    if (!nextValues) return;

    setOtp(nextValues);
    const filledCount = nextValues.filter(Boolean).length;
    const nextFocusIndex = Math.min(Math.max(filledCount, 1), otpLength) - 1;
    otpRefs.current[nextFocusIndex]?.focus();
  }, [otpLength]);

  const handleVerifyOtp = useCallback(async () => {
    const enteredOtp = otp.join('');
    if (enteredOtp.length < otpLength) {
      showWarningToast('Incomplete OTP', 'Please enter the 6-digit code.');
      return;
    }

    if (!submittedEmail) {
      showWarningToast('Missing email', 'Please restart the forgot password flow.');
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const response = await verifyForgotPasswordOtp({
        email: submittedEmail,
        otp: enteredOtp,
      });

      if (!response.resetToken) {
        showWarningToast('Invalid response', 'Please request a new OTP and try again.');
        return;
      }

      setResetToken(response.resetToken);
      setStep('reset');
      showSuccessToast('OTP verified', 'Create your new password.');
    } catch (error) {
      showErrorToast(error, 'OTP verification failed');
    } finally {
      setIsVerifyingOtp(false);
    }
  }, [otp, otpLength, submittedEmail]);

  const watchedPassword = resetPasswordForm.watch('password');
  const passwordChecks = useMemo(() => ({
    length: watchedPassword.length >= 12,
    uppercase: /[A-Z]/.test(watchedPassword),
    number: /\d/.test(watchedPassword),
  }), [watchedPassword]);

  const handleResetPassword = useCallback(async (data: ResetPasswordFormValues) => {
    if (!submittedEmail || !resetToken) {
      showWarningToast('Missing reset details', 'Please verify OTP again.');
      return;
    }

    setIsResetting(true);
    try {
      await resetForgotPassword({
        email: submittedEmail,
        resetToken,
        newPassword: data.password,
      });
      showSuccessToast('Password reset successful', 'You can now log in with your new password.');
      navigate('/login');
    } catch (error) {
      showErrorToast(error, 'Password reset failed');
    } finally {
      setIsResetting(false);
    }
  }, [navigate, resetToken, submittedEmail]);

  return (
    <div className="h-dvh overflow-hidden bg-[#e9edf1] p-6 sm:p-8">
      <div className="grid h-full min-h-0 grid-cols-1 grid-rows-1 overflow-hidden rounded-2xl border border-slate-200 lg:grid-cols-2">
        <aside className="relative hidden lg:block">
          <img
            src={nutritionistSignupVisual}
            alt="Scale your nutrition practice with intelligent meal planning"
            className="h-full w-full object-cover"
          />
          <div className="pointer-events-none absolute inset-0 flex flex-col justify-between p-10 xl:p-14">
            <h2 className="max-w-[560px] pt-6 text-[clamp(2.8rem,4.4vw,5.2rem)] font-medium leading-[0.95] tracking-[-0.01em] text-white">
              <span className="whitespace-nowrap">Scale your nutrition</span>
              <br />
              <span className="whitespace-nowrap">practice 3X</span>
            </h2>

            <div className="flex flex-1 items-center justify-center">
              <img src={nutritionOnboarding} alt="" aria-hidden="true" className="max-h-[72%] w-auto max-w-[94%] object-contain" />
            </div>

            <div className="max-w-[560px] pb-4">
              <p className="whitespace-nowrap text-[clamp(2.1rem,3.1vw,3.5rem)] font-medium leading-[1.04] text-white">
                Intelligent Meal Planning
              </p>
              <p className="mt-3 text-[clamp(1.15rem,1.55vw,2rem)] leading-[1.35] text-white/75">
                Create personalized, condition-specific meal plans in minutes - not hours.
              </p>
            </div>
          </div>
        </aside>

        <main className="flex items-center justify-center bg-white p-8 sm:p-10 lg:p-12">
          <div className="w-full max-w-[460px]">
            <img src={menuBarLogo} alt="Calorie Science" className="mb-8 h-10 w-auto max-w-[220px]" />

            {step === 'otp' ? (
              <form
                className="space-y-5"
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleVerifyOtp();
                }}
              >
                <h1 className="text-4xl font-semibold leading-tight text-slate-800">OTP Verification</h1>
                <p className="text-sm leading-6 text-slate-500">
                  Enter the six-digit code sent on your Email ID
                  {submittedEmail ? ` (${submittedEmail}).` : '.'}
                </p>

                <div className="flex items-center gap-3">
                  {otp.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(element) => {
                        otpRefs.current[index] = element;
                      }}
                      value={digit}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="done"
                      maxLength={otpLength}
                      pattern="[0-9]*"
                      onChange={(event) => handleOtpChange(index, event.target.value)}
                      onKeyDown={(event) => handleOtpKeyDown(index, event)}
                      onPaste={handleOtpPaste}
                      className="!h-12 !w-12 !rounded-md !border-slate-300 !bg-white !p-0 text-lg font-semibold [&_.MuiOutlinedInput-input]:!p-0 [&_.MuiOutlinedInput-input]:!text-center"
                      aria-label={`OTP digit ${index + 1}`}
                    />
                  ))}
                </div>

                <Button
                  type="submit"
                  disabled={isVerifyingOtp}
                  className="!h-11 !w-full !rounded-md !bg-[#20AEDC] !text-base !font-semibold !text-white hover:!bg-[#1a99c0]"
                >
                  {isVerifyingOtp ? 'Verifying...' : 'Verify'}
                </Button>

                <p className="text-sm font-medium text-slate-600">
                  Didn&apos;t receive email? Check your spam folder, or{' '}
                  <button
                    type="button"
                    onClick={handleResendLink}
                    disabled={isResendingOtp || resendCooldownSeconds > 0}
                    className="font-semibold text-[#0f7fa4] underline disabled:opacity-60"
                  >
                    {isResendingOtp
                      ? 'Resending...'
                      : resendCooldownSeconds > 0
                        ? `Resend in ${resendCooldownSeconds}s`
                        : 'Resend'}
                  </button>
                </p>

                <p className="text-center text-sm text-slate-600">
                  Back to{' '}
                  <Link to="/login" className="font-medium text-[#3198c4] hover:underline">
                    Login
                  </Link>
                </p>
              </form>
            ) : step === 'reset' ? (
              <>
                <h1 className="text-4xl font-semibold leading-tight text-slate-800">Create a New Password</h1>
                <p className="mt-2 text-sm text-slate-500">
                  Your new password must meet the requirements below.
                </p>

                <Form {...resetPasswordForm}>
                  <form onSubmit={resetPasswordForm.handleSubmit(handleResetPassword)} className="mt-6 space-y-4">
                    <FormField
                      control={resetPasswordForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-medium uppercase tracking-[0.06em] text-slate-500">
                            Password*
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              autoComplete="new-password"
                              placeholder="********"
                              className="!h-12 !rounded-md !border-slate-300 !bg-white text-sm"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={resetPasswordForm.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-medium uppercase tracking-[0.06em] text-slate-500">
                            Confirm Password*
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              autoComplete="new-password"
                              placeholder="********"
                              className="!h-12 !rounded-md !border-slate-300 !bg-white text-sm"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <div className="flex flex-wrap items-center gap-6 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <span
                          className={`h-4 w-4 rounded-full border ${passwordChecks.length ? 'border-[#20AEDC] bg-[#20AEDC]' : 'border-slate-400 bg-transparent'}`}
                        />
                        <span>12+ characters</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`h-4 w-4 rounded-full border ${passwordChecks.uppercase ? 'border-[#20AEDC] bg-[#20AEDC]' : 'border-slate-400 bg-transparent'}`}
                        />
                        <span>Uppercase</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`h-4 w-4 rounded-full border ${passwordChecks.number ? 'border-[#20AEDC] bg-[#20AEDC]' : 'border-slate-400 bg-transparent'}`}
                        />
                        <span>Number</span>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={isResetting}
                      className="!mt-5 !h-11 !w-full !rounded-md !bg-[#20AEDC] !text-base !font-semibold !text-white hover:!bg-[#1a99c0] disabled:!bg-[#a9d5e7]"
                    >
                      {isResetting ? 'Resetting...' : 'Reset Password'}
                    </Button>

                    <p className="text-center text-sm text-slate-600">
                      Back to{' '}
                      <Link to="/login" className="font-medium text-[#3198c4] hover:underline">
                        Login
                      </Link>
                    </p>
                  </form>
                </Form>
              </>
            ) : (
              <>
                <h1 className="text-4xl font-semibold leading-tight text-slate-800">Reset Your Password</h1>
                <p className="mt-2 text-sm text-slate-500">
                  Enter your registered email address and we&apos;ll send you an OTP.
                </p>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-4">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-medium uppercase tracking-[0.06em] text-slate-500">
                            Email*
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              autoComplete="email"
                              placeholder="Enter your email"
                              className="!h-12 !rounded-md !border-slate-300 !bg-white text-sm"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="!mt-4 !h-11 !w-full !rounded-md !bg-[#20AEDC] !text-base !font-semibold !text-white hover:!bg-[#1a99c0] disabled:!bg-[#a9d5e7]"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Send OTP'}
                    </Button>

                    <p className="text-center text-sm text-slate-600">
                      Remembered your password?{' '}
                      <Link to="/login" className="font-medium text-[#3198c4] hover:underline">
                        Login
                      </Link>
                    </p>
                  </form>
                </Form>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ForgotPassword;
